import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import styled from "styled-components";
import * as Yup from "yup";
import { fetchJobById, closeJob } from "../../../../api/jobs";
import Cache from "../../../../cache";
import { JobClosedCategory, JobClosedCategoryKey } from "../../../../types/job";
import BasePage from "../../../BasePage";
import Container from "../../../library/Container";
import {
  TextareaInputField,
  SelectField,
  FormError,
} from "../../../library/Form";
import SubmitButton from "../../../library/Form/SubmitButton";
import Skeleton from "../../../library/Skeleton";

const getJobIdFromPath = () => window.location.pathname.split("/")[3];

const Close = () => {
  const backUrl = `/employer/jobs/${getJobIdFromPath()}/applications/#/job-profile`;

  return (
    <BasePage title="Close Job" backUrl={backUrl}>
      <Container>
        <PageContent />
      </Container>
    </BasePage>
  );
};

const PageContent = () => {
  const [formError, setFormError] = useState<string>();
  const jobId = getJobIdFromPath();

  const { isLoading, data: job } = useQuery(["job", jobId], () =>
    fetchJobById(jobId)
  );

  const mutation = useMutation(closeJob);

  const submit = async (values: FormValues) => {
    setFormError(undefined);
    const { closedReason, closedReasonCategory } = values;

    const data = {
      jobId: job.id,
      closedReason,
      closedReasonCategory: closedReasonCategory as JobClosedCategoryKey,
    };

    await mutation.mutateAsync(data, {
      onSuccess: () => {
        // Invalidate the cache as we've updated one of the jobs.
        Cache.delete("job-list");
        window.location.href = `/employer/jobs?job_closed_id=${job.id}`;
      },
      onError: () => {
        setFormError(`Something went wrong closing the job`);
      },
    });
  };

  return (
    <div>
      {isLoading ? <Skeleton height="50" /> : <Heading>{job.title}</Heading>}

      {formError && <FormError>{formError}</FormError>}

      <Formik
        onSubmit={submit}
        initialValues={initialValues}
        validationSchema={validation}
      >
        <Form>
          <HelpParagraph>
            Sorry to hear you have to close this job. It&apos;s important that
            our recruiters get enough information on the reason for this,
            especially if they need to manage a candidates experience. Please
            select a headline reason from the dropdown and add a message for the
            recruiter.
          </HelpParagraph>
          <SelectField
            label="Reason for closing"
            hiddenLabel
            name="closedReasonCategory"
          >
            <option value="" disabled selected>
              Select a reason
            </option>
            {Object.entries(JobClosedCategory).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </SelectField>
          <TextareaInputField
            label="Please add more detail for the recruiters"
            placeholder="Add a message for the recruiter"
            name="closedReason"
          />
          <SubmitButton submittingText="Closing...">Close</SubmitButton>
        </Form>
      </Formik>
    </div>
  );
};

const Heading = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const HelpParagraph = styled.p`
  margin-bottom: 1rem;
`;

type FormValues = {
  closedReason: string;
  closedReasonCategory: JobClosedCategoryKey | "";
};

const initialValues: FormValues = {
  closedReason: "",
  closedReasonCategory: "",
};

const validation = Yup.object({
  closedReasonCategory: Yup.string().required(
    "Please select a reason for closing the job"
  ),
  closedReason: Yup.string()
    .required("Please add a message")
    .min(20, "Please enter at least 20 characters")
    .max(500, "Please keep this under 500 characters"),
});

export default Close;
